// src/components/PaymentItem.tsx

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaDownload } from 'react-icons/fa6';
import { PaymentDTO } from "../../types/types";
import { instance } from "../../api/axiosInstance";
import Cookies from "js-cookie";

interface PaymentItemProps {
  payment: PaymentDTO;
  formattedDate: string;
}

const PaymentItem: React.FC<PaymentItemProps> = ({ payment, formattedDate }) => {
  const navigate = useNavigate();

  const handleDownload = async () => {
    console.log("Download button clicked for product:", payment.product?.id);
    const accessToken = Cookies.get('access_token');

    if (!accessToken) {
      console.error('Authentication token is missing.');
      alert('인증 토큰이 없습니다. 로그인해주세요.');
      return;
    }

    if (!payment.product || !payment.product.id || !payment.product.name) {
      console.error('Product information is incomplete.');
      alert('다운로드할 상품 정보가 없습니다.');
      return;
    }

    try {
      const response = await instance.get('/auth/files/download', {
        params: { productId: payment.product.id }, // Pass productId as a query parameter
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        responseType: 'blob', // Ensure the response is treated as a blob
      });

      // Extract filename from Content-Disposition header if available
      let filename = `${payment.product.name}.zip`; // Default filename
      const disposition = response.headers['content-disposition'];
      if (disposition && disposition.includes('filename=')) {
        const filenameMatch = disposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      // Create a blob from the response data
      const blob = new Blob([response.data]);

      // Create a link element to trigger the download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename); // Set the filename
      document.body.appendChild(link);
      link.click();

      // Clean up
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download file:', error);
      alert('파일 다운로드에 실패했습니다. 다시 시도해주세요.');
    }
  };

  return (
    <div className="border p-4 mb-4 rounded-lg" key={payment.id}>
      <div className="flex items-start justify-between">
        {/* 상품 정보 */}
        <div className="flex items-start">
          {payment.product ? (
            <>
              <img
                src={`${process.env.REACT_APP_API_BASE_URL}${payment.product.image}`}
                alt={payment.product.name}
                className="w-20 h-20 object-cover mr-4"
              />
              <div>
                <p className="font-semibold mb-1 text-base flex">
                  {payment.product.name}
                  <span className="ml-[10px] text-[12px]">주문번호 {payment.orderId}</span>
                </p>
                <p className="text-gray-500 mb-1">
                  결제 수단 {payment.easyPayProvider}
                </p>
                <p className="text-gray-500">
                  결제 금액 {payment.totalAmount.toLocaleString()} 원
                </p>
              </div>
            </>
          ) : (
            <p className="text-red-500">상품 정보가 없습니다.</p>
          )}
        </div>

        {/* 버튼 */}
        <div className="flex flex-col space-y-2 w-[160px]">
          {payment.product && (
            <button
              onClick={handleDownload}
              className="bg-MainColor justify-center hover:bg-opacity-80 transition duration-300 flex items-center gap-2 text-white px-4 py-2 rounded"
            >
              <FaDownload/>
              <span className="text-sm font-medium tracking-wide">
                다운로드
              </span>
            </button>
          )}
          <button
            className={`bg-gray-200 text-gray-600 px-4 py-2 rounded ${
              !payment.product ? 'cursor-not-allowed opacity-50' : ''
            }`}
            onClick={() => {
              if (payment.product) {
                navigate(`/purchase/details/${payment.id}?date=${encodeURIComponent(formattedDate)}`, {state: {payment}});
              } else {
                alert('상세 정보를 불러올 수 없습니다.');
              }
            }}
            disabled={!payment.product} // Disable button if product is null
          >
            자세히보기
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentItem;
